import { FlexBox, useBreakpoint } from "@brg/ui";
import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { HolderContent, ImagePreviewSection } from "shared/components/ImagePreviewSection/ImagePreviewSection";
import { API, Book, Designer, Endpoints, Project, Team } from "@brg/api";
import { useResizedImageEnhanced } from "shared/hooks/useResizedImageEnhanced";
import { EmblemImgContainer, LettersAvatarWrapper } from './styles';
import { LayoutConst} from 'core';
import { AppTester } from "supporting";
import { computeDesigner } from "shared/helpers/computeData";
import useEmblemUploader from "../hooks/useEmblemUploader";
import { isTeamChannel } from "shared/pages/teams/const/ModerationTypes";
import { TeamStatusActiveArchived } from "shared/helpers/teamsStatus";
import { ChannelDefaultEmblem } from "./ChannelDefaultEmblem";

export interface BookChaptersEmptyProps {
    canCreateChapter: boolean;
    onSelectChaptersClick: () => void;
    onCreateChapterClick: () => void;
    book: Book;
    previewProject?: Project;
};  

const getImages = (team: Team, lang: string) => {
    if (!team) return [];
    //@ts-ignore
    const hasTitleEnBG = team?.titleImages_en?.length;
    //@ts-ignore
    return lang === 'en' && hasTitleEnBG ? team?.titleImages_en : team?.titleImages;

  }
const getTabImages = (activeTab: any[]) => {
    if (!activeTab) return [];
    //@ts-ignore
    return activeTab?.titleImages;

  }

export const EmblemContainer: FC<{
    team: Team,
    owner?: Designer<'Global' | 'Local'>,
    canEditEmblem?: boolean;
    currentTab?: string
}> = ({
    team,
    owner,
    canEditEmblem,
    currentTab
}) => {
    const { teamId } = team;
    const { t, i18n } = useTranslation();
    const is1280 = useBreakpoint(LayoutConst.screen.l_12);
    const is1199 = useBreakpoint(LayoutConst.screen.l);
    const is992 = useBreakpoint(LayoutConst.screen.m);
    const isSuperPuper = AppTester.isSuperPuper();

    const { isChannel } = isTeamChannel(team?.moderationType ?? 0);

    const fileInput = useEmblemUploader(teamId)

    const activeTab = useMemo(() => {
        return team?.tabs?.find(tab => tab.path === currentTab)
    }, [currentTab, team]) 
    const activeTabHasImage = !!activeTab?.titleImages?.length;
    const activeTabHasLink = !!activeTab?.coverLink;
    
    const teamEmblem = getImages(team, i18n.language);
    const teamTabEmblem = getTabImages(activeTab);

    const letterAvatar = (owner: Designer<'Global'>) => {

        const designer = computeDesigner(API.toLang(owner, i18n.language));
        const { surName, name } = designer;
        const _name = name?.length ? name : 'unnamed';
        const _sur = surName?.length ? surName : '';
        const letters = (_name.substr(0, 1) + (_sur && _sur?.length > 0 ? _sur.substr(0, 1) : '')).toUpperCase();

        return letters
    }

    const emblem = useMemo(() => {
        if (activeTabHasImage) {
            return teamTabEmblem;
        }
        if (isChannel) {
            if (!!teamEmblem?.length) return teamEmblem;
            return undefined
        }
        return teamEmblem;
    } , [teamEmblem, isChannel, owner, activeTabHasImage, currentTab])

    const isArchiveChannel = useMemo(() => {
        return TeamStatusActiveArchived(team) && isChannel && !!emblem
    } , [team, isChannel, emblem])

    const holderText = useMemo(() => {
        if (isArchiveChannel) {
            return t('subscription.in_archive')
        }

        return ''
    } , [isArchiveChannel])

    //@ts-ignore
    const coverLink = useMemo(() => (activeTabHasLink ? activeTab.coverLink : team?.coverLink?.path !== '' ? team.coverLink : null) , [team, activeTabHasLink])

    const fixedImgSize = useMemo(() => {
        if (is992) {
            return {
                width: `${(LayoutConst.projectCard.xs.w * 2) + LayoutConst.projectCardGap.tablet}`,
                height: 489
            }
        }
        if (is1199) {
            return {
                width: 348,
                height: 489
            }
        }
        return {
            width: 270,
            height: 380
        }
    }, [is992, is1199])

    const [image, ref] = useResizedImageEnhanced(emblem, fixedImgSize);

    const imageUrl = useMemo(() => {
        //@ts-ignore
        if (image?.url && !image?.isLocal) {
            return `${Endpoints.files}/${image.url}`
        }
        //@ts-ignore
        if (image?.isLocal) {
            return image?.url;
        }
        return ''
      },[image]);

    const handleDeleteTeamAvatarClick = useCallback(() => {
        if (!teamId) return false;
        //Не работает метод upload для teamId

    }, [teamId])

    if (team?.getEmblemComponent) {
        return team?.getEmblemComponent(fixedImgSize);
    }

    if (isChannel && !teamEmblem?.length) {
        return (
            <EmblemImgContainer 
                ref={ref} 
                imgShowed={Boolean(imageUrl)} 
                isSuperPuper={isSuperPuper}
            >
                <ChannelDefaultEmblem team={team}/>
            </EmblemImgContainer>
        )
    }

    if (!teamEmblem?.length) {
        return (
            <EmblemImgContainer ref={ref} imgShowed={Boolean(imageUrl)} isSuperPuper={isSuperPuper}>
            {/* {Tooltip} */}
            <ImagePreviewSection
                // custom={custom}
                {...fixedImgSize}
                // width={270}
                // height={380}
                widthMobile="calc(100vw - 20px)"
                heightMobile={"calc((100vw - 20px) * 1.407)"}
                buttonHeight={48}
                //@ts-ignore
                fileInput={fileInput}
                // onClick={fileInput.openFileClick}
                url={imageUrl}
                placeholderMargin={0}
                placeholderMaxWidth={300}
                placeholderHeight={120}
                isContextPage
                isTeamEmblem
                canEditEmblem={canEditEmblem}
                coverLink={coverLink}
                >
                    <HolderContent holderHeight={50} placeholderMargin={32} placeholderMaxWidth={250}>
                        <FlexBox direction={"column"} directionMobile={'column'} align={'center'}>
                        <LettersAvatarWrapper>
                            {letterAvatar(owner ?? {} as Designer<'Global'>)}
                        </LettersAvatarWrapper>
                        </FlexBox>
                    </HolderContent>
                </ImagePreviewSection>

            </EmblemImgContainer>
        )
    }

    if (!emblem) {
        return null;
    }
    
    return  (
        <EmblemImgContainer ref={ref} imgShowed={Boolean(imageUrl)} isSuperPuper={isSuperPuper}>
            {/* {Tooltip} */}
            <ImagePreviewSection
                // custom={custom}
                {...fixedImgSize}
                // width={270}
                // height={380}
                widthMobile="calc(100vw - 20px)"
                heightMobile={"calc((100vw - 20px) * 1.407)"}
                buttonHeight={48}
                //@ts-ignore
                fileInput={fileInput}
                // onClick={fileInput.openFileClick}
                url={imageUrl}
                placeholderMargin={0}
                placeholderMaxWidth={300}
                placeholderHeight={120}
                isContextPage
                isTeamEmblem
                holderText={holderText}
                canEditEmblem={canEditEmblem}
                coverLink={coverLink}
            >
                    <HolderContent holderHeight={50} placeholderMargin={32} placeholderMaxWidth={250}>
                        <FlexBox direction={"column"} directionMobile={'column'} align={'center'}>
                            <FlexBox offsetBottom={24} width={250} className={'empty-holder'}>
                                {t('project_edit.image.text')}
                            </FlexBox>
                        </FlexBox>
                    </HolderContent>  
                </ImagePreviewSection>
        </EmblemImgContainer>
    );
};import { FlexBox, useBreakpoint } from "@brg/ui";
import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { HolderContent, ImagePreviewSection } from "shared/components/ImagePreviewSection/ImagePreviewSection";
import { API, Book, Designer, Endpoints, Project, Team } from "@brg/api";
import { useResizedImageEnhanced } from "shared/hooks/useResizedImageEnhanced";
import { EmblemImgContainer, LettersAvatarWrapper } from './styles';
import { LayoutConst} from 'core';
import { AppTester } from "supporting";
import { computeDesigner } from "shared/helpers/computeData";
import useEmblemUploader from "../hooks/useEmblemUploader";
import { isTeamChannel } from "shared/pages/teams/const/ModerationTypes";
import { TeamStatusActiveArchived } from "shared/helpers/teamsStatus";
import { ChannelDefaultEmblem } from "./ChannelDefaultEmblem";

export interface BookChaptersEmptyProps {
    canCreateChapter: boolean;
    onSelectChaptersClick: () => void;
    onCreateChapterClick: () => void;
    book: Book;
    previewProject?: Project;
};  

const getImages = (team: Team, lang: string) => {
    if (!team) return [];
    //@ts-ignore
    const hasTitleEnBG = team?.titleImages_en?.length;
    //@ts-ignore
    return lang === 'en' && hasTitleEnBG ? team?.titleImages_en : team?.titleImages;

  }
const getTabImages = (activeTab: any[]) => {
    if (!activeTab) return [];
    //@ts-ignore
    return activeTab?.titleImages;

  }

export const EmblemContainer: FC<{
    team: Team,
    owner?: Designer<'Global' | 'Local'>,
    canEditEmblem?: boolean;
    currentTab?: string
}> = ({
    team,
    owner,
    canEditEmblem,
    currentTab
}) => {
    const { teamId } = team;
    const { t, i18n } = useTranslation();
    const is1280 = useBreakpoint(LayoutConst.screen.l_12);
    const is1199 = useBreakpoint(LayoutConst.screen.l);
    const is992 = useBreakpoint(LayoutConst.screen.m);
    const isSuperPuper = AppTester.isSuperPuper();

    const { isChannel } = isTeamChannel(team?.moderationType ?? 0);

    const fileInput = useEmblemUploader(teamId)

    const activeTab = useMemo(() => {
        return team?.tabs?.find(tab => tab.path === currentTab)
    }, [currentTab, team]) 
    const activeTabHasImage = !!activeTab?.titleImages?.length;
    const activeTabHasLink = !!activeTab?.coverLink;
    
    const teamEmblem = getImages(team, i18n.language);
    const teamTabEmblem = getTabImages(activeTab);

    const letterAvatar = (owner: Designer<'Global'>) => {

        const designer = computeDesigner(API.toLang(owner, i18n.language));
        const { surName, name } = designer;
        const _name = name?.length ? name : 'unnamed';
        const _sur = surName?.length ? surName : '';
        const letters = (_name.substr(0, 1) + (_sur && _sur?.length > 0 ? _sur.substr(0, 1) : '')).toUpperCase();

        return letters
    }

    const emblem = useMemo(() => {
        if (activeTabHasImage) {
            return teamTabEmblem;
        }
        if (isChannel) {
            if (!!teamEmblem?.length) return teamEmblem;
            return undefined
        }
        return teamEmblem;
    } , [teamEmblem, isChannel, owner, activeTabHasImage, currentTab])

    const isArchiveChannel = useMemo(() => {
        return TeamStatusActiveArchived(team) && isChannel && !!emblem
    } , [team, isChannel, emblem])

    const holderText = useMemo(() => {
        if (isArchiveChannel) {
            return t('subscription.in_archive')
        }

        return ''
    } , [isArchiveChannel])

    //@ts-ignore
    const coverLink = useMemo(() => (activeTabHasLink ? activeTab.coverLink : team?.coverLink?.path !== '' ? team.coverLink : null) , [team, activeTabHasLink])

    const fixedImgSize = useMemo(() => {
        if (is992) {
            return {
                width: `${(LayoutConst.projectCard.xs.w * 2) + LayoutConst.projectCardGap.tablet}`,
                height: 489
            }
        }
        if (is1199) {
            return {
                width: 348,
                height: 489
            }
        }
        return {
            width: 270,
            height: 380
        }
    }, [is992, is1199])

    const [image, ref] = useResizedImageEnhanced(emblem, fixedImgSize);

    const imageUrl = useMemo(() => {
        //@ts-ignore
        if (image?.url && !image?.isLocal) {
            return `${Endpoints.files}/${image.url}`
        }
        //@ts-ignore
        if (image?.isLocal) {
            return image?.url;
        }
        return ''
      },[image]);

    const handleDeleteTeamAvatarClick = useCallback(() => {
        if (!teamId) return false;
        //Не работает метод upload для teamId

    }, [teamId])

    if (team?.getEmblemComponent) {
        return team?.getEmblemComponent(fixedImgSize);
    }

    if (isChannel && !teamEmblem?.length) {
        return (
            <EmblemImgContainer 
                ref={ref} 
                imgShowed={Boolean(imageUrl)} 
                isSuperPuper={isSuperPuper}
            >
                <ChannelDefaultEmblem team={team}/>
            </EmblemImgContainer>
        )
    }

    if (!teamEmblem?.length) {
        return (
            <EmblemImgContainer ref={ref} imgShowed={Boolean(imageUrl)} isSuperPuper={isSuperPuper}>
            {/* {Tooltip} */}
            <ImagePreviewSection
                // custom={custom}
                {...fixedImgSize}
                // width={270}
                // height={380}
                widthMobile="calc(100vw - 20px)"
                heightMobile={"calc((100vw - 20px) * 1.407)"}
                buttonHeight={48}
                //@ts-ignore
                fileInput={fileInput}
                // onClick={fileInput.openFileClick}
                url={imageUrl}
                placeholderMargin={0}
                placeholderMaxWidth={300}
                placeholderHeight={120}
                isContextPage
                isTeamEmblem
                canEditEmblem={canEditEmblem}
                coverLink={coverLink}
                >
                    <HolderContent holderHeight={50} placeholderMargin={32} placeholderMaxWidth={250}>
                        <FlexBox direction={"column"} directionMobile={'column'} align={'center'}>
                        <LettersAvatarWrapper>
                            {letterAvatar(owner ?? {} as Designer<'Global'>)}
                        </LettersAvatarWrapper>
                        </FlexBox>
                    </HolderContent>
                </ImagePreviewSection>

            </EmblemImgContainer>
        )
    }

    if (!emblem) {
        return null;
    }
    
    return  (
        <EmblemImgContainer ref={ref} imgShowed={Boolean(imageUrl)} isSuperPuper={isSuperPuper}>
            {/* {Tooltip} */}
            <ImagePreviewSection
                // custom={custom}
                {...fixedImgSize}
                // width={270}
                // height={380}
                widthMobile="calc(100vw - 20px)"
                heightMobile={"calc((100vw - 20px) * 1.407)"}
                buttonHeight={48}
                //@ts-ignore
                fileInput={fileInput}
                // onClick={fileInput.openFileClick}
                url={imageUrl}
                placeholderMargin={0}
                placeholderMaxWidth={300}
                placeholderHeight={120}
                isContextPage
                isTeamEmblem
                holderText={holderText}
                canEditEmblem={canEditEmblem}
                coverLink={coverLink}
            >
                    <HolderContent holderHeight={50} placeholderMargin={32} placeholderMaxWidth={250}>
                        <FlexBox direction={"column"} directionMobile={'column'} align={'center'}>
                            <FlexBox offsetBottom={24} width={250} className={'empty-holder'}>
                                {t('project_edit.image.text')}
                            </FlexBox>
                        </FlexBox>
                    </HolderContent>  
                </ImagePreviewSection>
        </EmblemImgContainer>
    );
};